import React, { useEffect, useState } from 'react';

import FileViewer from 'containers/FileView/FileViewer';
import { useSelector } from 'react-redux';

import selectSessionToken from '../../selectors/sessionToken';



interface FileItem {
  name: string;
  type: 'file' | 'directory';
  path: string;
  url?: string;
}

const FileStorage: React.FC = () => {
  const [items, setItems] = useState<FileItem[]>([]);
  const [error, setError] = useState('');
  const [currentPath, setCurrentPath] = useState('');
  const [viewingFile, setViewingFile] = useState<{ path: string; name: string } | null>(null);
  const sessionToken = useSelector(selectSessionToken);
  
  useEffect(() => {  

    fetch(`/api/files?path=${encodeURIComponent(currentPath)}`, {
      headers: {
        Authorization: `Bearer ${sessionToken}`,
      },
    })
      .then(res => {
        if (res.status === 401) {
          setItems([]);
          setError('Unauthorized');
          return null;
        }
        return res.json();
      })
      .then(data => {
        if (!data) return;
        if (data.error) {
          setError(data.error);
        } else {
          setItems(data.items || []);
          setError('');
          window.scrollTo(0, 0);
        }
      })
      .catch(() => {
        setError('Error fetching files.');
      });
  }, [currentPath, sessionToken]);


  const handleClick = (item: FileItem) => {
    if (item.type === 'directory') {
      setCurrentPath(item.path);
    } else {      
      // Open file in viewer component instead of new window
      setViewingFile({ path: item.path, name: item.name });
    }
  };


  const parts = currentPath.split('/').filter(Boolean);
  const breadcrumbs = [
    { label: 'Home', path: '' },
    ...parts.map((p, idx) => ({
      label: p,
      path: parts.slice(0, idx + 1).join('/'),
    })),
  ];

  return (
    <div style={{ padding: 24 }}>
      <h1 style={{ marginBottom: 16 }}>File Storage</h1>

      {/* Breadcrumb Navigation */}
      <div style={{ marginBottom: 20 }}>
        {breadcrumbs.map((crumb, i) => (
          <span key={crumb.path}>
            <span
              onClick={() => setCurrentPath(crumb.path)}
              style={{
                color: i === breadcrumbs.length - 1 ? '#222' : '#0078d4',
                cursor: i === breadcrumbs.length - 1 ? 'default' : 'pointer',
                fontWeight: i === breadcrumbs.length - 1 ? 600 : 400,
                marginRight: 6,
              }}
            >
              {crumb.label}
            </span>
            {i !== breadcrumbs.length - 1 && <span>/ </span>}
          </span>
        ))}
      </div>

      {error ? (
        <div style={{ color: 'red' }}>⚠️ {error}</div>
      ) : items.length === 0 ? (
        <p>No files found in this folder.</p>
      ) : (
        <ul style={{ listStyle: 'none', paddingLeft: 0 }}>
          {items.map(item => (
            <li
              key={item.path}
              onClick={() => handleClick(item)}
              style={{
                cursor: 'pointer',
                margin: '8px 0',
                padding: '6px 4px',
                borderRadius: 4,
                color: item.type === 'directory' ? '#0078d4' : '#333',
                fontWeight: item.type === 'directory' ? 600 : 400,
              }}
            >
              {item.type === 'directory' ? '📁' : '📄'} {item.name}
            </li>
          ))}
        </ul>
      )}

      {/* File Viewer Modal */}
      {viewingFile && (
        <FileViewer
          filePath={viewingFile.path}
          fileName={viewingFile.name}
          onClose={() => setViewingFile(null)}
        />
      )}
    </div>
  );
};

export default FileStorage;